import {DownloadIcon} from '@heroicons/react/outline';
import {useState} from 'react';

import {buildDownloadFileName} from '@afriex/core/utils';

import {Button} from '@/components';
import {ratesService} from '@/services/ratesService';
import type {AfriexQueryParams} from '@/types';
import {saveBlob} from '@/utils/downloadFile';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

interface DownloadOTCTradeCSVButtonProps {
  /** The filters the OTC trade list on screen was fetched with. */
  filters: AfriexQueryParams;
}

/**
 * Downloads every OTC trade matching the list's filters: the server streams
 * the CSV (see adminOTCTradeCsv.ts) and this only saves it. It used to export
 * a fixed "last N days, first 100 trades" fetch made when the page loaded.
 */
const DownloadOTCTradeCSVButton = ({
  filters,
}: DownloadOTCTradeCSVButtonProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await ratesService.downloadOTCTradeList(filters);
      saveBlob(blob, buildDownloadFileName('otc_trades', 'csv'));
    } catch (error) {
      showErrorMessage(getErrorMessage(error, 'Failed to export OTC trades'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      colorScheme="cyan"
      variant="outline"
      disabled={isDownloading}
      isLoading={isDownloading}
      onClick={() => {
        // handleDownload reports its own failures, so this only fires if that
        // reporting itself throws.
        handleDownload().catch(logRejection('Failed to export OTC trades'));
      }}
      testId="download-otc-trades-button"
    >
      <DownloadIcon className="h-5 w-5" />
    </Button>
  );
};

export default DownloadOTCTradeCSVButton;
